import React, { useState } from 'react';
import { Phone, Mail, MapPin, Instagram, Send, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import BookingModal from './BookingModal';

const Contact = () => {
  const { t } = useTranslation();
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [formData, setFormData] = useState({
    parentName: '',
    email: '',
    phone: '',
    childAge: '',
    startPeriod: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Call backend API
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    fetch(`${API_URL}/api/contact`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        parent_name: formData.parentName,
        email: formData.email,
        phone: formData.phone,
        child_age: formData.childAge,
        start_period: formData.startPeriod,
        message: formData.message
      })
    })
    .then(response => response.json())
    .then(data => {
      toast.success(data.message || t('contact.successMessage'));
      setFormData({
        parentName: '',
        email: '',
        phone: '',
        childAge: '',
        startPeriod: '',
        message: ''
      });
    })
    .catch(error => {
      console.error('Error:', error);
      toast.error(t('contact.errorMessage'));
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-xl text-gray-600 mb-4">
            {t('contact.subtitle')}
          </p>
          <p className="text-lg text-pink-600 font-semibold mb-6">
            {t('contact.cta')}
          </p>
          
          {/* Quick Booking Button */}
          <Button
            size="lg"
            onClick={() => setIsBookingOpen(true)}
            className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
          >
            <Calendar className="mr-2 w-5 h-5" />
            {t('contact.bookNow')}
          </Button>
          
          <p className="text-sm text-gray-500 mt-4">
            {t('contact.orText')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="space-y-6">
            <Card className="border-0 shadow-lg bg-gradient-to-br from-pink-50 to-purple-50">
              <CardContent className="p-8 space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.infoTitle')}</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                        <MapPin className="w-6 h-6 text-pink-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">{t('contact.infoTitle')}</p>
                        <p className="text-gray-700">{t('contact.address')}</p>
                        <p className="text-sm text-gray-600">{t('contact.postalCode')}</p>
                        <p className="text-sm text-gray-600">{t('contact.district')}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                        <Phone className="w-6 h-6 text-pink-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">Telefono</p>
                        <a href={`tel:${t('contact.phone')}`} className="text-gray-700 hover:text-pink-600 transition-colors">
                          {t('contact.phone')}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                        <Mail className="w-6 h-6 text-pink-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">Email</p>
                        <a href={`mailto:${t('contact.email')}`} className="text-gray-700 hover:text-pink-600 transition-colors">
                          {t('contact.email')}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                        <Instagram className="w-6 h-6 text-pink-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">Instagram</p>
                        <a 
                          href={`https://instagram.com/${t('contact.instagram')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-700 hover:text-pink-600 transition-colors"
                        >
                          @{t('contact.instagram')}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-pink-200">
                  <h4 className="font-semibold text-gray-900 mb-3">Orari di apertura</h4>
                  <p className="text-gray-700">Lunedì - Venerdì: 7:50 - 16:30</p>
                  <p className="text-sm text-gray-600 mt-2">Aperto tutto l'anno (escluse 2 settimane ad agosto)</p>
                </div>
              </CardContent>
            </Card>

            {/* Map */}
            <Card className="border-0 shadow-lg overflow-hidden">
              <CardContent className="p-0">
                <div className="w-full h-96 bg-gray-200">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2865.8247!2d12.8956!3d43.9178!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132d1f4c7e8b5555%3A0x1234567890abcdef!2sVia%20Faggi%2C%201%2C%2061122%20Pesaro%20PU!5e0!3m2!1sit!2sit!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Mappa 4 KIDS"
                  ></iframe>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.formTitle')}</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="parentName" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.formName')} *
                  </label>
                  <Input
                    id="parentName"
                    name="parentName"
                    type="text"
                    required
                    value={formData.parentName}
                    onChange={handleChange}
                    placeholder={t('contact.formName')}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.formEmail')} *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('contact.formEmail')}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.formPhone')} *
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t('contact.formPhone')}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="childAge" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.formChildAge')}
                  </label>
                  <Input
                    id="childAge"
                    name="childAge"
                    type="text"
                    value={formData.childAge}
                    onChange={handleChange}
                    placeholder={t('contact.formChildAge')}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="startPeriod" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.formStartPeriod')}
                  </label>
                  <Input
                    id="startPeriod"
                    name="startPeriod"
                    type="text"
                    value={formData.startPeriod || ''}
                    onChange={handleChange}
                    placeholder={t('contact.formStartPeriod')}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.formMessage')}
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('contact.formMessage')}
                    className="w-full"
                  />
                </div>

                <Button 
                  type="submit"
                  size="lg"
                  className="w-full bg-[#E91E8C] hover:bg-[#d01a7a] text-white py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  <Send className="mr-2 w-5 h-5" />
                  {t('contact.formSubmit')}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  Compilando questo modulo accetti di essere contattato dal nostro team.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Booking Modal */}
      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </section>
  );
};

export default Contact;
